import * as z from 'zod/mini';
import { identifierSchema } from '../naming.ts';
import {
  idSchema,
  nameSchema,
  shortTextSchema,
  sortOrderSchema,
  timestampSchema,
} from './common.ts';

/** What kind of place a property is; it decides the icon the map draws. */
export const PROPERTY_KINDS = ['business', 'residential'] as const;
export type PropertyKind = (typeof PROPERTY_KINDS)[number];
export const propertyKindSchema = z.enum(PROPERTY_KINDS);
export const PROPERTY_KIND_LABELS: Readonly<Record<PropertyKind, string>> = {
  business: 'Business',
  residential: 'Residential',
};

export const propertySchema = z.object({
  id: idSchema,
  clientId: idSchema,
  name: z.string(),
  /** Short path segment; blank only in a cache made before migration 0051. */
  identifier: z._default(z.string(), ''),
  kind: propertyKindSchema,
  addressLine1: z.string(),
  addressLine2: z.string(),
  city: z.string(),
  region: z.string(),
  postalCode: z.string(),
  /** Where the address geocodes to, when the Worker could look it up. */
  latitude: z._default(z.nullable(z.number()), null),
  longitude: z._default(z.nullable(z.number()), null),
  /** When the address was last looked up (null: never), whether or not it was found. */
  placedAt: z._default(z.nullable(timestampSchema), null),
  /** Site access anyone may read: where to park, how to get in, when the site can be visited. */
  parking: z.string(),
  entry: z.string(),
  hours: z.string(),
  sortOrder: sortOrderSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Property = z.infer<typeof propertySchema>;

/** The access facts a property carries in the clear (the codes are sealed: property-access.ts). */
export const PROPERTY_ACCESS_FIELDS = ['parking', 'entry', 'hours'] as const;
export const PROPERTY_ACCESS_LABELS: Readonly<
  Record<(typeof PROPERTY_ACCESS_FIELDS)[number], string>
> = {
  parking: 'Parking',
  entry: 'How to get in',
  hours: 'Hours',
};

// A patch is a partial of the plain fields, so an edit that leaves the kind
// or an access field out does not reset it.
const propertyFields = {
  name: nameSchema,
  identifier: z.optional(identifierSchema),
  kind: propertyKindSchema,
  addressLine1: shortTextSchema,
  addressLine2: shortTextSchema,
  city: shortTextSchema,
  region: shortTextSchema,
  postalCode: shortTextSchema,
  parking: shortTextSchema,
  entry: shortTextSchema,
  hours: shortTextSchema,
};

export const propertyInputSchema = z.object({
  ...propertyFields,
  kind: z._default(propertyFields.kind, 'business'),
  parking: z._default(propertyFields.parking, ''),
  entry: z._default(propertyFields.entry, ''),
  hours: z._default(propertyFields.hours, ''),
});
export type PropertyInput = z.infer<typeof propertyInputSchema>;

export const propertyPatchSchema = z.partial(z.object(propertyFields));
export type PropertyPatch = z.infer<typeof propertyPatchSchema>;
